import styled, { css } from "styled-components";
import { Icon as BaseIcon } from "../Icon";
import { style } from "../style";
import { focusOutline } from "./focusOutlineStyle";

type ContainerProps = {
  checked: boolean;
  disabled: boolean;
  isBoxMode: boolean;
  hasIcon: boolean;
  className?: string;
  hasDescription?: boolean;
};

export const Container = styled.label<ContainerProps>`
  ${(props) =>
    props.isBoxMode
      ? `
        border: 1px solid;
        border-radius: 3px;
        min-width: 300px;
        max-width: 1000px;
        grid-template-rows: auto 1fr;
        padding: 16px;
      `
      : `
        grid-template-rows: auto;
        padding: 10px 0;
      `};
  border-color: ${(props) => (props.checked ? style.colors.blue.dark : style.colors.grey.dark)};
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  opacity: ${(props) => (props.disabled ? style.vars.disabledOpacity : "1")};
  ${({ checked, isBoxMode }) =>
    checked &&
    isBoxMode &&
    css`
      background-color: ${style.colors.blue.lightest};
    `}

  display: grid;
  ${({ hasIcon, hasDescription }) => {
    let repeat = 1;
    if (hasIcon && hasDescription) {
      repeat = 2;
    }

    return css`
      grid-template-columns: 16px ${hasIcon ? "32px" : ""} repeat(${repeat}, auto);
    `;
  }}

  grid-column-gap: 8px;

  ${(props) =>
    !props.disabled &&
    css`
      &:hover {
        input {
          border-color: ${style.colors.blue.dark};
          box-shadow: 0 0 0 ${style.vars.borderWidth.thin} ${style.colors.blue.primary};
        }
      }
    `};

  &:active,
  &:focus,
  &:focus-within {
    outline: 0;

    &:not([disabled]) {
      ${({ isBoxMode }) =>
        isBoxMode
          ? focusOutline()
          : focusOutline({
              top: "-" + style.vars.whitespace.small,
              bottom: "-" + style.vars.whitespace.small,
            })}
    }
  }
`;

type LoadingWrapperProps = {
  hasIcon: boolean;
};

export const LoadingWrapper = styled.div<LoadingWrapperProps>`
  grid-row: 1 / -1;
  grid-column-start: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: ${(props) => (props.hasIcon ? "center" : "flex-start")};
`;

export const Icon = styled.img`
  grid-row: 1 / -1;
  grid-column: 2;
  align-self: center;
  justify-self: center;
`;

export const SvgIcon = styled(BaseIcon)`
  grid-row: 1 / -1;
  grid-column: 2;
  align-self: center;
  justify-self: center;
  height: ${style.vars.space["5"]};
  width: ${style.vars.space["5"]};
`;

type LabelTextProps = {
  checked: boolean;
  isBoxMode?: boolean;
  hasDescription?: boolean;
};

export const LabelText = styled.div<LabelTextProps>`
  user-select: none;
  margin: 0;
  font-weight: ${(props) => (props.isBoxMode ? style.vars.fontWeight.bold : style.vars.fontWeight.base)};
  color: ${({ checked }) => (checked ? `${style.colors.blue.dark}` : `${style.colors.grey.darkest}`)};
  align-self: ${(props) => (props.isBoxMode ? "start" : "center")};
  text-transform: none;
  letter-spacing: 0;
  font-size: ${style.vars.fontSize.base};

  ${({ isBoxMode, hasDescription }) =>
    isBoxMode &&
    !hasDescription &&
    css`
      grid-row-start: 2;
      grid-column-end: -1;
      align-self: center;
    `}
`;

type DescriptionTextProps = {
  $hasIcon: boolean;
};

export const DescriptionText = styled.span<DescriptionTextProps>`
  grid-row-start: 2;
  grid-column-end: ${({ $hasIcon }) => ($hasIcon ? "-2" : "-1")};
  margin: 0;
  font-size: ${style.vars.fontSize.small};
  color: ${style.colors.grey.dark};
  user-select: none;
  text-transform: none;
  letter-spacing: 0;
  font-weight: ${style.vars.fontWeight.base};
`;
